import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { RouteComponentProps, useLocation } from "react-router";
import Button from "../../../components/Button";
import Error from "../../../components/Error";
import Info from "../../../components/Info";
import Loading from "../../../components/Loading";
import WeduTooltip from "../../../components/WeduTooltip/intex";
import { useWizard } from "../../wizard/context";
import { AcademicSecondary } from "../components/AcademicSecondary";
import AcademicsFilter from "../components/AcademicsFilter";
import AcademicsFilterFormSecondary from "../components/AcademicsFilterFormSecondary";
import { useAcademicsPageSecondary } from "../hooks/useAcademicsPageSecondary";
import { useAcademicsPageStyles } from "../styles/useAcademicsPageStyles";
import { IAcademicsContext } from "../types";
import { endOfTitle } from "../../../constants/constants";
import { useCheckForViewModal } from "../hooks/useCheckForViewModal";
import queryString from "querystring";

const AcademicsSecondaryContext = createContext({} as IAcademicsContext);
export const useAcademicsSecondaryContext = (): IAcademicsContext =>
  useContext(AcademicsSecondaryContext);

const AcademicsPageSecondary: FC<RouteComponentProps> = () => {
  const classes = useAcademicsPageStyles();
  const { openWizard } = useWizard();
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [highlightedAcademics, setHighlightedAcademics] = useState<string[]>(
    [],
  );

  const {
    isLoading,
    isError,
    errorMessage,
    applyFilter,
    academics,
    refetchAcademics,
    academicWizardConfigs,
    programWizardConfigs,
    intakeWizardConfigs,
  } = useAcademicsPageSecondary();

  useCheckForViewModal({
    getViewAcademicConfig: academicWizardConfigs.getViewConfig,
    getViewProgramConfig: programWizardConfigs.getViewConfig,
  });

  const contextValue = useRef<IAcademicsContext>({
    academicWizardConfigs,
    programWizardConfigs,
    intakeWizardConfigs,
    refetchAcademics,
  });

  const addAcademic = useCallback(async () => {
    (await openWizard(academicWizardConfigs.getCreateConfig())) &&
      (await refetchAcademics());
  }, []);

  const submitHandler = useCallback((data) => {
    setIsFilterApplied(true);
    applyFilter(data);
  }, []);

  const location = useLocation();

  useEffect(() => {
    const queryParams = queryString.parse(
      location.search.split("?").join(""),
      " ",
    );

    if (typeof queryParams.highlightedAcademics === "string") {
      setHighlightedAcademics(queryParams.highlightedAcademics.split(","));
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Academics{endOfTitle}</title>
      </Helmet>
      <AcademicsSecondaryContext.Provider value={contextValue.current}>
        {isLoading && <Loading />}
        <div>
          <Container maxWidth={"lg"} className={classes.root}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <div className={classes.stickyContainer}>
                  <AcademicsFilter
                    submitHandler={submitHandler}
                    filterForm={AcademicsFilterFormSecondary}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={9}>
                {!isLoading && (
                  <>
                    <Container
                      maxWidth="lg"
                      className={classes.buttonContainer}
                    >
                      <WeduTooltip title="Add academic">
                        <Button
                          color={"success"}
                          variant={"contained"}
                          onClick={addAcademic}
                          className={classes.button}
                        >
                          Add
                        </Button>
                      </WeduTooltip>
                    </Container>
                    {isError ? (
                      <Error text={errorMessage} />
                    ) : academics.length > 0 || isFilterApplied ? (
                      <Container maxWidth="lg" className={classes.container}>
                        {academics.length > 0 ? (
                          <div className={classes.listContainer}>
                            {academics.map((academic) => (
                              <AcademicSecondary
                                key={academic.id}
                                academic={academic}
                                highlighted={highlightedAcademics.includes(
                                  academic.id,
                                )}
                              />
                            ))}
                          </div>
                        ) : (
                          <Info
                            className={classes.info}
                            icon={"search"}
                            text={"You have no results for now."}
                          />
                        )}
                      </Container>
                    ) : (
                      <Container maxWidth="lg" className={classes.container}>
                        <Info
                          className={classes.info}
                          text={
                            "Step 1: Create Branches.\nStep 2: Complete Academics and assign to branches."
                          }
                        />
                      </Container>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </Container>
        </div>
      </AcademicsSecondaryContext.Provider>
    </>
  );
};

export default AcademicsPageSecondary;
